/**
 * App raiz — login full-screen ou AppShell com áreas (Financeiro/Mesa).
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { AppShell } from "./components/layout/AppShell";
import { MobileShell } from "./components/mobile/MobileShell";
import type { AppPage } from "./components/layout/Sidebar";
import type { PdfScreen } from "./components/layout/TopBar";
import { SubTabs, type SubTabSpec } from "./components/ui/SubTabs";
import { Loading } from "./components/Loading";
import { FinanceiroDrePage } from "./pages/FinanceiroDrePage";
import { FinanceiroDreContabilPage } from "./pages/FinanceiroDreContabilPage";
import { FinanceiroDespesasPage } from "./pages/FinanceiroDespesasPage";
import { FinanceiroFluxoPage } from "./pages/FinanceiroFluxoPage";
import { FinanceiroContasReceberPage } from "./pages/FinanceiroContasReceberPage";
import { CfPorBookCard } from "./components/financeiro/CfPorBookCard";
import { MesaOperacoesPage } from "./pages/MesaOperacoesPage";
import { VarejoPage, type VarejoTab } from "./pages/VarejoPage";
import { RhPage } from "./pages/RhPage";
import { LoginPage } from "./pages/LoginPage";
import { DrePdfProvider } from "./contexts/DrePdfContext";
import { refreshAllCaches } from "./utils/refreshAllCaches";
import { useAuth } from "./hooks/useAuth";

type FinTab = "fluxo" | "contasReceber" | "despesas" | "cfBook" | "dre" | "dreContabil" | "indicadores" | "orcado";
type MesaTab = "pnl" | "exposicao" | "fa" | "curva" | "pld" | "juros" | "simulador";

const FIN_TABS: SubTabSpec<FinTab>[] = [
  { id: "fluxo", label: "Fluxo de Caixa" },
  { id: "contasReceber", label: "Contas a Receber" },
  { id: "despesas", label: "Despesas" },
  // Task #64: aba própria ao lado de Despesas (1 card; dentro de Despesas
  // misturaria Omie/D1-D10 com CF contratado Zeus de 10 anos).
  { id: "cfBook", label: "CF por Book" },
  { id: "dre", label: "DRE Gerencial" },
  { id: "dreContabil", label: "DRE Contábil" },
  { id: "indicadores", label: "Indicadores", locked: true },
  { id: "orcado", label: "Orçado vs Realizado", locked: true },
];

const FIN_BREADCRUMB: Record<FinTab, string> = {
  fluxo: "Fluxo de Caixa",
  contasReceber: "Contas a Receber",
  despesas: "Despesas",
  cfBook: "CF por Book",
  dre: "DRE Gerencial",
  dreContabil: "DRE Contábil",
  indicadores: "Indicadores",
  orcado: "Orçado vs Realizado",
};

const MESA_BREADCRUMB: Record<MesaTab, string> = {
  pnl: "PnL",
  exposicao: "Exposição",
  fa: "FA",
  curva: "Curva de Mercado",
  pld: "Controle PLD",
  juros: "Juros & Inflação",
  simulador: "Simulador",
};

const VAREJO_BREADCRUMB: Record<VarejoTab, string> = {
  visao: "Visão Geral",
  abc: "Curva ABC",
  valuation: "Valuation",
  viva: "Carteira Viva",
};

// #246: Fluxo NÃO exporta PDF — o "Relatório PDF" gerava mock desconexo da
// tela (decisão Veiga: esconder agora; cabear no dado real = task futura).
// Só DRE Gerencial (v2 real) e DRE Contábil (banner Preview) têm export.
function pdfScreenOf(tab: FinTab): PdfScreen | undefined {
  if (tab === "dre") return "dre";
  if (tab === "dreContabil") return "dreContabil";
  return undefined;
}

// Mobile Fase 1 (UX 03/07): corte 1024 -> 768 — iPad retrato (768px) ve o
// app COMPLETO; a casca mobile fica so pra telefone (<768).
const DESKTOP_MIN_W = 768;

function useViewport() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window === "undefined" ? true : window.innerWidth >= DESKTOP_MIN_W,
  );
  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN_W);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return isDesktop;
}

export default function App() {
  const auth = useAuth();
  const [page, setPage] = useState<AppPage>("financeiro");
  const [finTab, setFinTab] = useState<FinTab>("fluxo");
  const [mesaTab, setMesaTab] = useState<MesaTab>("pnl");
  const [varejoTab, setVarejoTab] = useState<VarejoTab>("visao");
  // Task #124: drill-down DRE → Despesas. Clicar numa linha D1-D10 no DRE
  // navega pra aba Despesas com a linha pré-filtrada. Navegação manual nas
  // subtabs limpa o filtro (handleFinTab).
  const [despesaLinha, setDespesaLinha] = useState<string | null>(null);
  // Task #196: "Atualizar" da TopBar. Bumpa este nonce → a view (children do
  // AppShell) remonta via `key`, re-disparando os hooks de fetch; antes
  // limpamos os caches module-level pra o refetch ir à rede (não ao cache).
  // Sem window.location.reload (que resetava a navegação React pro default).
  const [refreshNonce, setRefreshNonce] = useState(0);
  const isDesktop = useViewport();

  const handleRefresh = useCallback(async () => {
    refreshAllCaches();
    setRefreshNonce((n) => n + 1);
    // Janela mínima de feedback do spinner; os hooks resolvem em paralelo nessa
    // janela (cada um atualiza a tela ao chegar). Sem query client pra "await
    // refetch", então damos um piso visível em vez de um await preciso.
    await new Promise((resolve) => setTimeout(resolve, 600));
  }, []);

  const handleFinTab = (t: FinTab) => {
    setDespesaLinha(null);
    setFinTab(t);
  };
  const drillToDespesa = (linha: string) => {
    setDespesaLinha(linha);
    setFinTab("despesas");
  };

  if (!auth.ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-indra-azul">
        <Loading label="Conectando ao Cortex…" />
      </div>
    );
  }

  if (!auth.authed) {
    return <LoginPage onAuthenticated={auth.onAuthenticated} />;
  }

  // Task #228 + #31: mobile (innerWidth<768) ganha uma experiência DEDICADA
  // (cards full-width, touch-friendly) com nav inferior entre Resumo · Fluxo ·
  // PLD (read-only). O app denso completo (sidebar/tabelas/entrada) segue
  // exclusivo do desktop (>=768 — iPad retrato incluso, Fase 1).
  if (!isDesktop) {
    return <MobileShell onLogout={auth.logout} />;
  }

  const breadcrumb =
    page === "financeiro"
      ? ["Financeiro", FIN_BREADCRUMB[finTab]]
      : page === "varejo"
        ? ["Varejo", VAREJO_BREADCRUMB[varejoTab]]
        : page === "rh"
          ? ["RH", "Pessoal e custo"]
          : ["Mesa de Operações", MESA_BREADCRUMB[mesaTab]];

  const pdfScreen = page === "financeiro" ? pdfScreenOf(finTab) : undefined;
  // PDF: oculto na Mesa (sem export). Com #196 o próprio `pdfScreen=undefined`
  // já esconde o botão; mantemos hidePdf=isMesa explícito por clareza.
  // "Atualizar": #88/#90 o ocultavam na Mesa porque era window.location.reload
  // (redundante com F5). #196 o tornou refresh in-place; #196-ajuste RE-EXIBE na
  // Mesa — dados mais voláteis do dashboard (PnL/Exposição/FA/Curva intra-day).
  const isMesa = page === "mesa";

  return (
    <DrePdfProvider>
      <AppShell
        breadcrumb={breadcrumb}
        onLogout={auth.logout}
        userIniciais="TV"
        userNome={auth.user ?? "Thiago Veiga"}
        pdfScreen={pdfScreen}
        onRefresh={handleRefresh}
        hidePdf={isMesa}
        activePage={page}
        onNavigate={setPage}
      >
        {/* Task #196: key=refreshNonce → "Atualizar" remonta a view (hooks
            re-fetcham). A navegação (page/finTab/mesaTab) é estado de App fora
            do Fragment, então o usuário permanece na mesma aba. */}
        <Fragment key={refreshNonce}>
          {page === "financeiro" && (
            <>
              <SubTabs tabs={FIN_TABS} active={finTab} onChange={handleFinTab} />
              <div className="mt-6">
                {finTab === "fluxo" && <FinanceiroFluxoPage />}
                {finTab === "contasReceber" && <FinanceiroContasReceberPage />}
                {finTab === "despesas" && <FinanceiroDespesasPage linhaInicial={despesaLinha} />}
                {finTab === "cfBook" && <CfPorBookCard />}
                {finTab === "dre" && <FinanceiroDrePage onDrillDownDespesa={drillToDespesa} />}
                {finTab === "dreContabil" && <FinanceiroDreContabilPage />}
              </div>
            </>
          )}

          {page === "mesa" && <MesaOperacoesPage onTabChange={setMesaTab} />}

          {page === "varejo" && <VarejoPage onTabChange={setVarejoTab} />}

          {page === "rh" && <RhPage />}
        </Fragment>
      </AppShell>
    </DrePdfProvider>
  );
}
